import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Avatar,
    IconButton,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Pagination
} from "@mui/material";
import Swal from "sweetalert2";
import { styled } from "@mui/material/styles";
import {
    Person,
    Phone,
    Email,
    LocationOn,
    ArrowBack,
    ArrowForward,
    Verified,
    Delete,
    Edit,
    Lock,
    AdminPanelSettings,
    Search
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { registerUser, getAllUsers, updateUser, deleteUser, uploadUserPhoto, BASE_URL } from "../services/customerApi";

const StyledCard = styled(Card)({
    borderRadius: 16,
    background: "#111111", // Dark background for cards
    color: "#ffffff",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    border: "1px solid rgba(253, 184, 19, 0.1)"
});

const AnimatedFormField = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        fontFamily: "'Outfit', sans-serif",
        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
        "&:hover fieldset": { borderColor: "#FDB813" },
        "&.Mui-focused fieldset": { borderColor: "#FDB813" },
        "& input": {
            color: "#ffffff",
            "&:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 100px #111111 inset",
                WebkitTextFillColor: "#ffffff",
                transition: "background-color 5000s ease-in-out 0s",
            }
        },
    },
    "& .MuiInputLabel-root": {
        color: "rgba(255, 255, 255, 0.7)",
        fontFamily: "'Outfit', sans-serif",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#FDB813",
    }
});

const avatarList = Array.from({ length: 120 }, (_, i) => `avatar_${i + 1}.png`);

const initialForm = {
    name: "",
    phone: "",
    location: "",
    email: "",
    userName: "",
    passWord: "",
    role: "EMPLOYEE",
    profilePic: ""
};

const getAvatarSrc = (picStr) => {
    if (!picStr) return "";
    if (picStr.startsWith("http")) return picStr;
    if (picStr.startsWith("avatar_")) {
        try {
            return require(`../assets/avatars/${picStr}`);
        } catch (e) {
            return "";
        }
    }
    return `${BASE_URL}${picStr}`;
};

export default function SystemUserManagement({ section }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState(initialForm);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
    const [page, setPage] = useState(0);
    const rowsPerPage = 5;
    const [searchTerm, setSearchTerm] = useState("");
    const [phoneError, setPhoneError] = useState("");

    // Step 0 valid: name, phone (10 digits, no error), location filled
    const isStep0Valid =
        !!formData.name.trim() &&
        !!formData.phone.trim() &&
        formData.phone.replace(/\D/g, "").length === 10 &&
        !phoneError &&
        !!formData.location.trim();

    // Step 1 valid: username and password filled
    const isStep1Valid =
        !!formData.userName.trim() &&
        !!formData.passWord.trim();

    useEffect(() => {
        if (section === "user-details") {
            setPage(0);
            fetchUsers();
        }
    }, [section]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await getAllUsers();
            setUsers(data);
        } catch (err) {
            console.error("Failed to fetch users", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentUserInfo = async () => {
        try {
            const allUsers = await getAllUsers();
            const loggedUserName = sessionStorage.getItem("userName");
            const current = allUsers.find(u => u.userName === loggedUserName);
            if (current) setEditUser(current);
        } catch (err) {
            console.error("Failed to fetch profile", err);
        }
    };

    useEffect(() => {
        if (section === "profile") {
            fetchCurrentUserInfo();
        }
    }, [section]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            // Only allow digits, max 10
            const digitsOnly = value.replace(/\D/g, "");
            if (digitsOnly.length > 10) return;
            setFormData({ ...formData, phone: digitsOnly });
            if (digitsOnly.length > 0 && digitsOnly.length < 10) {
                setPhoneError("Phone number must be 10 digits");
            } else {
                setPhoneError("");
            }
            return;
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleEditChange = (e) => {
        setEditUser({ ...editUser, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            await registerUser(formData);
            setFormData(initialForm);
            setPhoneError("");
            setActiveStep(0);
            await Swal.fire({
                icon: "success",
                title: "Awesome!",
                text: "User Created Successfully",
                confirmButtonColor: "#4CAF50"
            });
        } catch (err) {
            console.error("Creation failed", err);
            const msg = err.response && err.response.status === 409
                ? "This username is already taken."
                : "Failed to create user. Please try again.";
            await Swal.fire({
                icon: "error",
                title: "Oops!",
                text: msg,
                confirmButtonColor: "#e53935"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);
            await updateUser(editUser.id, editUser);
            setEditDialogOpen(false);
            fetchUsers();
            await Swal.fire({
                icon: "success",
                title: "Awesome!",
                text: "User Updated Successfully",
                confirmButtonColor: "#4CAF50"
            });
        } catch (err) {
            console.error("Update failed", err);
            const msg = err.response && err.response.status === 409
                ? "Username already taken."
                : "Failed to update user.";
            await Swal.fire({
                icon: "error",
                title: "Oops!",
                text: msg,
                confirmButtonColor: "#e53935"
            });
        } finally {
            setLoading(false);
        }
    };



    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Delete User?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e53935",
            cancelButtonColor: "#555",
            confirmButtonText: "Yes, delete it!"
        });
        if (result.isConfirmed) {
            try {
                await deleteUser(id);
                fetchUsers();
                await Swal.fire({
                    icon: "success",
                    title: "Deleted!",
                    text: "User has been deleted successfully.",
                    confirmButtonColor: "#4CAF50"
                });
            } catch (err) {
                console.error("Delete failed", err);
                Swal.fire({ icon: "error", title: "Oops!", text: "Failed to delete user.", confirmButtonColor: "#e53935" });
            }
        }
    };

    const steps = ["Personal Info", "Account Details"];

    if (section === "user-creation") {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, pt: { xs: 8.75, md: 11.25 } }}>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 4, fontFamily: "'Outfit', sans-serif", color: "#ffffff", fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
                    User Creation
                </Typography>
                <StyledCard>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <Stepper activeStep={activeStep} sx={{ mb: 5, overflowX: "auto", pb: 1, "& .MuiStepIcon-root.Mui-active": { color: "#FDB813" }, "& .MuiStepIcon-root.Mui-completed": { color: "#FDB813" } }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel sx={{ "& .MuiStepLabel-label": { color: "rgba(255,255,255,0.7)", fontSize: { xs: "0.75rem", md: "1rem" } }, "& .MuiStepLabel-label.Mui-active": { color: "#ffffff", fontWeight: 700 } }}>
                                        {label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <AnimatePresence mode="wait">
                            {activeStep === 0 ? (
                                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                    <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                                        <Box sx={{ position: "relative" }}>
                                            <Avatar
                                                src={getAvatarSrc(formData.profilePic)}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    bgcolor: "#FDB813",
                                                    color: "#111111",
                                                    fontSize: "2rem",
                                                    fontWeight: 800,
                                                    cursor: "pointer",
                                                    boxShadow: "0 4px 14px 0 rgba(0,0,0,0.39)",
                                                    "&:hover": { opacity: 0.8 },
                                                    border: "2px solid #FDB813"
                                                }}
                                                onClick={() => setAvatarDialogOpen(true)}
                                            >
                                                {!formData.profilePic && (formData.name?.charAt(0) || "?")}
                                            </Avatar>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: "absolute",
                                                    bottom: -4,
                                                    right: -4,
                                                    bgcolor: "#FDB813",
                                                    color: "#111111",
                                                    "&:hover": { bgcolor: "#e5a100" }
                                                }}
                                                onClick={() => setAvatarDialogOpen(true)}
                                            >
                                                <Edit sx={{ fontSize: "1rem" }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <AnimatedFormField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <AnimatedFormField
                                                fullWidth
                                                label="Mobile Number"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                error={!!phoneError}
                                                helperText={phoneError || (formData.phone && formData.phone.length < 10 ? `${formData.phone.length}/10 digits` : "")}
                                                inputProps={{ maxLength: 10 }}
                                                InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <AnimatedFormField fullWidth label="Location" name="location" value={formData.location} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <AnimatedFormField fullWidth label="Email Address" name="email" value={formData.email} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }} />
                                        </Grid>
                                    </Grid>
                                </motion.div>
                            ) : (
                                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <AnimatedFormField fullWidth inputProps={{ autoComplete: 'new-password' }} label="Username" name="userName" value={formData.userName} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <AnimatedFormField fullWidth inputProps={{ autoComplete: 'new-password' }} type="password" label="Password" name="passWord" value={formData.passWord} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <AnimatedFormField select fullWidth label="Role" name="role" value={formData.role} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><AdminPanelSettings sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}>
                                                <MenuItem value="ADMIN">ADMIN</MenuItem>
                                                <MenuItem value="EMPLOYEE">EMPLOYEE</MenuItem>
                                                <MenuItem value="MARKETER">MARKETER</MenuItem>
                                            </AnimatedFormField>
                                        </Grid>
                                    </Grid>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 5 }}>
                            <Button disabled={activeStep === 0} onClick={() => setActiveStep(0)} startIcon={<ArrowBack />} sx={{ color: "#ffffff" }}>Back</Button>
                            {activeStep === 0 ? (
                                <Button
                                    variant="contained"
                                    onClick={() => setActiveStep(1)}
                                    endIcon={<ArrowForward />}
                                    disabled={!isStep0Valid}
                                    sx={{ bgcolor: "#FDB813", color: "#111111", fontWeight: 800, "&:hover": { bgcolor: "#e5a100" }, "&.Mui-disabled": { bgcolor: "rgba(253,184,19,0.3)", color: "rgba(17,17,17,0.4)" } }}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={loading || !isStep1Valid}
                                    startIcon={<Verified />}
                                    sx={{ bgcolor: "#FDB813", color: "#111111", fontWeight: 800, "&:hover": { bgcolor: "#e5a100" }, "&.Mui-disabled": { bgcolor: "rgba(253,184,19,0.3)", color: "rgba(17,17,17,0.4)" } }}
                                >
                                    {loading ? "Creating..." : "Create User"}
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </StyledCard>

                {/* Avatar Selection Dialog */}
                <Dialog
                    open={avatarDialogOpen}
                    onClose={() => setAvatarDialogOpen(false)}
                    PaperProps={{
                        sx: {
                            bgcolor: "#ffffff",
                            color: "#000000",
                            borderRadius: 3,
                            width: "320px",
                            maxWidth: "90vw"
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 800, textAlign: "center", borderBottom: 1, borderColor: "divider", p: 1.5, fontSize: "1.1rem" }}>
                        Select Profile
                    </DialogTitle>
                    <DialogContent sx={{ p: 1.5, maxHeight: "45vh", overflowY: "auto", '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#cccccc', borderRadius: '3px' } }}>
                        <Grid container spacing={1}>
                            {avatarList.map((avatar) => (
                                <Grid item xs={3} key={avatar} sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Box
                                        onClick={() => {
                                            setFormData({ ...formData, profilePic: avatar });
                                            setAvatarDialogOpen(false);
                                        }}
                                        sx={{
                                            cursor: "pointer",
                                            borderRadius: "50%",
                                            p: 0.25,
                                            border: formData.profilePic === avatar ? "2px solid #111111" : "2px solid transparent",
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": { transform: "scale(1.1)", borderColor: formData.profilePic === avatar ? "#111111" : "rgba(0, 0, 0, 0.2)" }
                                        }}
                                    >
                                        <Avatar
                                            src={getAvatarSrc(avatar)}
                                            sx={{ width: 48, height: 48 }}
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 1, borderTop: 1, borderColor: "divider", justifyContent: "center" }}>
                        <Button onClick={() => setAvatarDialogOpen(false)} sx={{ color: "#000000", fontWeight: 700, fontSize: "0.85rem" }}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        );
    }

    if (section === "profile") {
        return (
            <Box sx={{
                p: { xs: 2, md: 4 },
                pt: { xs: 8.75, md: 2.25 },
                display: "flex", mb: 4,
                flexDirection: "column",
                alignItems: "center"
            }}>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 4, fontFamily: "'Outfit', sans-serif", color: "#ffffff", textAlign: "center" }}>
                    My Profile
                </Typography>
                <StyledCard sx={{ width: "100%", maxWidth: 800 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 4 }}>
                            <Box sx={{ position: "relative", mb: 2 }}>
                                <Avatar
                                    src={getAvatarSrc(editUser?.profilePic)}
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        bgcolor: "#FDB813",
                                        color: "#111111",
                                        fontSize: "2.5rem",
                                        fontWeight: 800,
                                        border: "4px solid rgba(253, 184, 19, 0.3)"
                                    }}
                                >
                                    {editUser?.name?.charAt(0) || "?"}
                                </Avatar>
                            </Box>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h5" fontWeight={800} sx={{ color: "#FDB813", mb: 0.5 }}>{editUser?.name || "Loading..."}</Typography>
                                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>{editUser?.role} • {editUser?.userName}</Typography>
                            </Box>
                        </Box>

                        <Grid container spacing={3} justifyContent="center">
                            <Grid item xs={12} sm={8} md={6}>
                                <AnimatedFormField
                                    fullWidth
                                    label="Full Name"
                                    name="name"
                                    value={editUser?.name || ""}
                                    InputProps={{ readOnly: true }}
                                    inputProps={{ style: { textAlign: 'center' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6}>
                                <AnimatedFormField fullWidth label="Phone" name="phone" value={editUser?.phone || ""} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'center' } }} />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6}>
                                <AnimatedFormField fullWidth label="Email" name="email" value={editUser?.email || ""} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'center' } }} />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6}>
                                <AnimatedFormField fullWidth label="Location" name="location" value={editUser?.location || ""} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'center' } }} />
                            </Grid>
                        </Grid>

                    </CardContent>
                </StyledCard>
            </Box>
        );
    }

    const filteredUsers = users.filter(u => 
        !searchTerm || 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        u.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const displayedUsers = filteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, pt: { xs: 8.75, md: 11.25 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h4" fontWeight={800} sx={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff", fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
                    User Details
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                    sx={{
                        width: { xs: "100%", sm: "300px" },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            background: "rgba(255,255,255,0.05)",
                            "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                            "&:hover fieldset": { borderColor: "#FDB813" },
                            "&.Mui-focused fieldset": { borderColor: "#FDB813" }
                        },
                        "& input": { color: "#ffffff", fontFamily: "'Outfit', sans-serif" }
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search sx={{ color: "rgba(255,255,255,0.5)" }} /></InputAdornment>
                    }}
                />
            </Box>
            <TableContainer component={StyledCard} sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead sx={{ background: "#111111" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Username</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Password</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Phone</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Location</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "#ffffff", whiteSpace: "nowrap" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedUsers.map((u) => (
                            <TableRow
                                key={u.id}
                                hover
                                sx={{
                                    transition: "all 0.3s ease",
                                    '&:hover': {
                                        backgroundColor: 'rgba(253, 184, 19, 0.03) !important',
                                        boxShadow: 'inset 4px 0 0 #FDB813, 0 4px 15px rgba(0,0,0,0.2)',
                                        transform: 'scale(1.002)'
                                    }
                                }}
                            >
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <Avatar src={getAvatarSrc(u.profilePic)} sx={{ bgcolor: alpha("#667eea", 0.8), width: 32, height: 32, fontSize: "0.8rem" }}>
                                            {!u.profilePic && (u.name?.charAt(0) || u.userName?.charAt(0))}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{u.name || "N/A"}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{u.userName}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{u.passWord}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{u.phone}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{u.email || "N/A"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{u.location || "N/A"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                    <Typography variant="caption" sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 2,
                                        bgcolor: u.role === "ADMIN" ? "#111111" : "#FDB813",
                                        color: u.role === "ADMIN" ? "#ffffff" : "#111111",
                                        fontWeight: 800,
                                        fontSize: "0.7rem",
                                        letterSpacing: 0.5
                                    }}>
                                        {u.role}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                    <IconButton size="small" sx={{ color: "#FDB813" }} onClick={() => { setEditUser(u); setEditDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                                    <IconButton size="small" sx={{ color: "#ff4d4d" }} onClick={() => handleDelete(u.id)}><Delete fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ 
                display: "flex", 
                flexDirection: { xs: "column", sm: "row" }, 
                justifyContent: "space-between", 
                alignItems: "center", 
                mt: 3,
                gap: 2
            }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Poppins', sans-serif", textAlign: { xs: "center", sm: "left" } }}>
                    Showing {displayedUsers.length} of {filteredUsers.length} users • Page {page + 1} of {Math.ceil(filteredUsers.length / rowsPerPage)}
                </Typography>
                <Pagination
                    page={page + 1}
                    count={Math.ceil(filteredUsers.length / rowsPerPage)}
                    onChange={(e, v) => setPage(v - 1)}
                    size="small"
                    siblingCount={0}
                    boundaryCount={1}
                    sx={{
                        "& .MuiPaginationItem-root": { color: "#ffffff", borderColor: "rgba(255,255,255,0.2)" },
                        "& .Mui-selected": { bgcolor: "#FDB813 !important", color: "#111111", fontWeight: 800 }
                    }}
                    showFirstButton
                    showLastButton
                />
            </Box>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                sx={{
                    "& .MuiDialog-paper": {
                        margin: { xs: 2, sm: 4 },
                        width: { xs: "calc(100% - 32px)", sm: "auto" }
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", md: "1.25rem" } }}>Edit User Details</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <AnimatedFormField
                                fullWidth
                                label="Full Name"
                                name="name"
                                value={editUser?.name || ""}
                                onChange={handleEditChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AnimatedFormField
                                fullWidth
                                label="Username"
                                name="userName"
                                value={editUser?.userName || ""}
                                onChange={handleEditChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AnimatedFormField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={editUser?.phone || ""}
                                onChange={handleEditChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AnimatedFormField
                                fullWidth
                                label="Email"
                                name="email"
                                value={editUser?.email || ""}
                                onChange={handleEditChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AnimatedFormField
                                fullWidth
                                label="Location"
                                name="location"
                                value={editUser?.location || ""}
                                onChange={handleEditChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AnimatedFormField
                                select
                                fullWidth
                                label="Role"
                                name="role"
                                value={editUser?.role || ""}
                                onChange={handleEditChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><AdminPanelSettings sx={{ color: "rgba(255, 255, 255, 0.7)" }} /></InputAdornment> }}
                            >
                                <MenuItem value="ADMIN">ADMIN</MenuItem>
                                <MenuItem value="EMPLOYEE">EMPLOYEE</MenuItem>
                                <MenuItem value="MARKETER">MARKETER</MenuItem>
                            </AnimatedFormField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdate}
                        disabled={loading}
                        sx={{
                            bgcolor: "#FDB813",
                            color: "#111111",
                            fontWeight: 800,
                            "&:hover": { bgcolor: "#e5a100" }
                        }}
                    >
                        {loading ? "Updating..." : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
